// The room's clip operations, as one thing a page can hold.
//
// Copy, cut, paste, duplicate, split, delete, nudge, change lane, mute — reachable
// from a hotkey, a button, or a thumb, and behaving identically whichever asked.
//
// Placement follows Live's Arrangement View: a paste lands at the insert
// marker, a duplicate right after the selection, and whatever either lands on
// is overwritten (trimmed, split, or deleted) rather than dodged — `land`, the
// page's lib/live-edits.ts `overwrite`. Neither re-snaps where it lands: the
// marker and the selection were put on the grid when they were drawn, and
// snapping again is what dropped a quarter-beat clip's duplicate on top of it.
//
// Two kinds of selection, as in Live. CLIPS (a title bar clicked) carry whole
// clips. A span of TIME (a lane or a clip's body dragged over) carries what
// lies inside it, clips cropped at its edges, and its own length — so a beat
// out of a bar copies, and a bar with a rest in it duplicates bar by bar.
// When both are set (a span selects the clips wholly inside it, so they can
// be dragged), the span is what copy, paste, duplicate, delete and split mean.
//
// Everything decidable lives in lib/clip-ops.ts and web-shared's
// lib/time-selection.ts, pure and tested; this holds only what needs React:
// the clipboard, a line of text to say what an operation declined to do, and
// the call to `propose`.
//
// The clipboard is CANONICAL RECORDS, not ids and not the system clipboard. An
// upsert carries a whole thirteen-field ClipData, so rebuilding one from the
// rendered lane model would blank warp markers and loop points (lib/edits.ts's
// own rule 1); and a clip's audioKey is a key into THIS room's pool, so
// carrying one out to another tab would carry a reference nothing there can
// resolve.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CanonicalClipState, CanonicalRoom } from "@shared/sync/state.js";
import type { Intent } from "@shared/sync/intents.js";
import {
  copyable,
  deleteClips,
  moveClips,
  moveClipsToLane,
  pasteClipBlock,
  setClipFields,
} from "@web-shared/lib/edits";
import { splitClipsAt, splitMissNote, type LaneSpan } from "@web-shared/lib/live-edits";
import {
  blockLaneMap,
  blockOfClips,
  clearSpan,
  clipsInSpan,
  copySpan,
  isSpan,
  lanesInOrder,
  placeBlock,
  splitAtSpan,
  type ClipBlock,
  type TimeSpan,
} from "@web-shared/lib/time-selection";
import {
  NOTHING_TO_COPY,
  NOTHING_TO_PASTE,
  NO_LANE_TO_PASTE_ONTO,
  SPAN_UNCUTTABLE,
  copiedNote,
  copiedSpanNote,
  cutNote,
  cutSpanNote,
  deletedNote,
  duplicateAt,
  liveSelection,
  muteTarget,
  noLaneNote,
  nudgeDelta,
  pasteLaneMap,
  pasteTargetLane,
  pastedNote,
} from "@/lib/clip-ops";

/** How long a note stays up. Long enough to read a sentence, short enough that
 *  it is gone before the next gesture wants the space. */
const NOTE_MS = 4000;

export interface ClipOpsArgs {
  room: CanonicalRoom | null;
  /** The selected clip ids, as the page holds them. */
  selected: ReadonlySet<string>;
  setSelected: (next: Set<string>) => void;
  /** Where edits go. Local or connected — the operations cannot tell. */
  editor: { propose: (intents: Intent[]) => void; busy?: boolean };
  /** The grid in force, in beats (0 = snapping off): a nudge's step. */
  grid: number;
  /** The room's tempo — an unwarped clip cut at its head is rebased onto it. */
  tempo: number;
  /**
   * The insert marker (Live's blue line), READ when an operation runs rather
   * than passed as a number, so nothing here rebuilds as it moves. Where a
   * paste lands and a split cuts.
   */
  insertBeats: () => number;
  /** Move the insert marker — a paste leaves it after what it pasted, so ⌘V
   *  again carries on. */
  setInsertBeats: (beats: number) => void;
  /** Propose placements over whatever they land on (Live's overwrite); the
   *  clips in `moving` travel with them, and `clear` is emptied as well.
   *  False when it couldn't. */
  land: (placements: Intent[], moving?: readonly string[], clear?: readonly LaneSpan[]) => boolean;
  /** The lane the phone's mixer strip has open — a paste target when nothing is
   *  selected, and often the only thing a thumb has said about "where". */
  fallbackLaneId: string | null;
  /** Live's time selection, or null. */
  timeSelection: TimeSpan | null;
  setTimeSelection: (next: TimeSpan | null) => void;
  /** The lane a click last put the insert marker on — the "here" of a paste. */
  cursorLaneId: string | null;
}

export interface ClipOps {
  /** The selection, resolved against the room: what every button acts on and
   *  what the count shown to a person counts. */
  selection: CanonicalClipState[];
  /** A time selection worth acting on. When set it, not `selection`, is what
   *  copy, cut, paste, duplicate, delete and split act on. */
  span: TimeSpan | null;
  clipboardSize: number;
  can: {
    copy: boolean;
    paste: boolean;
    /** The insert marker falls inside a selected clip, or the span crosses one. */
    split: boolean;
    /** Anything selected at all — nudge, lane, mute, delete. */
    act: boolean;
  };
  /** Whether the selection is currently muted, for the button's pressed state. */
  muted: boolean;
  copy: () => void;
  cut: () => void;
  paste: () => void;
  duplicate: () => void;
  /** Live's ⌘E: the selected clips cut at the insert marker, or every clip a
   *  time selection crosses cut at its edges. */
  split: () => void;
  remove: () => void;
  /** Along the timeline, in grid steps. */
  nudge: (steps: number) => void;
  moveLane: (direction: -1 | 1) => void;
  toggleMute: () => void;
  /** Why the last operation did what it did. Clears itself. */
  note: string | null;
}

export function useClipOps({
  room,
  selected,
  setSelected,
  editor,
  grid,
  tempo,
  insertBeats,
  setInsertBeats,
  land,
  fallbackLaneId,
  timeSelection,
  setTimeSelection,
  cursorLaneId,
}: ClipOpsArgs): ClipOps {
  const [clipboard, setClipboard] = useState<ClipBlock | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const noteTimer = useRef<number | undefined>(undefined);

  const say = useCallback((message: string) => {
    setNote(message);
    window.clearTimeout(noteTimer.current);
    noteTimer.current = window.setTimeout(() => setNote(null), NOTE_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(noteTimer.current), []);

  const selection = useMemo(() => liveSelection(room, selected), [room, selected]);
  const span = isSpan(timeSelection) ? timeSelection : null;
  // Where a paste goes when no selection says: the lane last clicked, else
  // the lane a thumb has open.
  const hereLaneId = cursorLaneId ?? fallbackLaneId;

  // What a paste or duplicate made becomes the selection once the room has it
  // (Live selects what you pasted), so ⌘D again lands after the copy rather
  // than on top of it.
  const [made, setMade] = useState<Array<{ trackId: string; start: number }> | null>(null);
  useEffect(() => {
    if (!made || !room) return;
    const ids = made.map(
      (m) => room.clips.find((c) => c.slot === undefined && c.trackId === m.trackId && Math.abs(c.startTime - m.start) < 1e-6)?.id,
    );
    if (ids.some((id) => id === undefined)) return;
    setMade(null);
    setSelected(new Set(ids as string[]));
  }, [made, room, setSelected]);
  const copyables = useMemo(() => selection.filter(copyable), [selection]);

  /** Take the selected clips onto the clipboard. Silent with nothing selected —
   *  the key was aimed at nothing — but vocal when there IS a selection and
   *  none of it can be carried, because that looks identical from the outside. */
  const copyToClipboard = useCallback((): CanonicalClipState[] => {
    if (!room || selection.length === 0) return [];
    if (copyables.length === 0) {
      say(NOTHING_TO_COPY);
      return [];
    }
    setClipboard(blockOfClips(room, copyables));
    return copyables;
  }, [room, copyables, selection.length, say]);

  /** Take the span onto the clipboard: what lies inside it, cropped to it, and
   *  its length. A span of silence copies too — pasted, it clears its span. */
  const copySpanToClipboard = useCallback((): { block: ClipBlock; skipped: number } | null => {
    if (!room || !span) return null;
    const copied = copySpan(room, span, tempo);
    if (copied.block.clips.length === 0 && copied.skipped > 0) {
      say(NOTHING_TO_COPY);
      return null;
    }
    setClipboard(copied.block);
    return copied;
  }, [room, span, tempo, say]);

  const copy = useCallback(() => {
    if (span) {
      const copied = copySpanToClipboard();
      if (copied) say(copiedSpanNote(copied.block.length, copied.block.clips.length, copied.skipped));
      return;
    }
    const carried = copyToClipboard();
    if (carried.length > 0) say(copiedNote(carried.length, selection.length));
  }, [span, copySpanToClipboard, copyToClipboard, say, selection.length]);

  /**
   * Land copied clips as a block at `atBeat`.
   *
   * Everything a person could be told is worked out BEFORE proposing, because
   * that is the only thing that works in both modes: the connected editor's
   * propose is fire-and-forget, so its return value says nothing about what the
   * room did with it.
   */
  const place = useCallback(
    (sources: CanonicalClipState[], atBeat: number, laneFor?: (c: CanonicalClipState) => string | null) => {
      if (!room) return;
      if (sources.length === 0) {
        say(NOTHING_TO_PASTE);
        return;
      }
      let map = laneFor;
      if (!map) {
        const lane = pasteTargetLane(room, selection, hereLaneId, sources);
        if (!lane) {
          say(NO_LANE_TO_PASTE_ONTO);
          return;
        }
        map = pasteLaneMap(room, sources, lane.id);
      }
      // Exactly where asked: whatever is there is overwritten, as in Live.
      const { intents, skipped } = pasteClipBlock(sources, map, Math.max(0, atBeat), (_lane, start) => start);
      if (intents.length === 0) {
        say(skipped > 0 ? NO_LANE_TO_PASTE_ONTO : NOTHING_TO_PASTE);
        return;
      }
      if (land(intents)) {
        say(pastedNote(intents.length, skipped));
        setTimeSelection(null);
        setMade(intents.flatMap((i) => (i.kind === "upsertClip" ? [{ trackId: i.trackId, start: i.clip.startTime }] : [])));
      }
    },
    [room, selection, hereLaneId, land, say, setTimeSelection],
  );

  /**
   * Land a copied span at `atBeat`: its clips at their distances from its
   * start, the whole of its time cleared on every lane it lands on. What it
   * covers becomes the time selection, as Live selects what it pasted.
   */
  const placeSpan = useCallback(
    (block: ClipBlock, atBeat: number, laneMap: ReadonlyMap<string, string | null>): TimeSpan | null => {
      const { placements, clears, landed, skipped } = placeBlock(block, atBeat, laneMap);
      if (!landed) {
        say(NO_LANE_TO_PASTE_ONTO);
        return null;
      }
      if (!land(placements, [], clears)) return null;
      if (skipped > 0) say(pastedNote(placements.length, skipped));
      setSelected(new Set());
      setTimeSelection(landed);
      return landed;
    },
    [land, say, setSelected, setTimeSelection],
  );

  /** Where a copied span's top lane lands: the selection's top lane, the lane
   *  last clicked, the selected clips', else back where it came from. */
  const spanTargetLane = useCallback(
    (block: ClipBlock): string | null => {
      if (!room) return null;
      const order = lanesInOrder(room).map((t) => t.id);
      const top = (ids: readonly string[]) => order.find((id) => ids.includes(id)) ?? null;
      return (span ? top(span.laneIds) : null) ?? hereLaneId ?? top(selection.map((c) => c.trackId)) ?? block.laneIds[0] ?? null;
    },
    [room, span, hereLaneId, selection],
  );

  const paste = useCallback(() => {
    if (!room || !clipboard) {
      say(NOTHING_TO_PASTE);
      return;
    }
    if (!clipboard.span) {
      place(clipboard.clips, insertBeats());
      return;
    }
    const lane = spanTargetLane(clipboard);
    if (!lane) {
      say(NO_LANE_TO_PASTE_ONTO);
      return;
    }
    const landed = placeSpan(clipboard, insertBeats(), blockLaneMap(room, clipboard, lane));
    // The marker follows the paste, so ⌘V again carries on after it.
    if (landed) setInsertBeats(landed.end);
  }, [room, clipboard, place, insertBeats, spanTargetLane, placeSpan, setInsertBeats, say]);

  // A duplicate never changes lane: each clip's copy lands behind it, on its own
  // lane, which is what "again" means for a piece of an arrangement. A span's
  // copy lands right after the SPAN — its length, not its clips' extent — and
  // the selection moves onto it, so ⌘D again carries on bar by bar.
  const duplicate = useCallback(() => {
    if (room && span) {
      const { block } = copySpan(room, span, tempo);
      const sameLanes = new Map(block.laneIds.map((id) => [id, id] as const));
      const landed = placeSpan(block, span.end, sameLanes);
      if (landed) setInsertBeats(landed.start);
      return;
    }
    place(copyables, duplicateAt(copyables), (c) => c.trackId);
  }, [room, span, tempo, placeSpan, setInsertBeats, place, copyables]);

  /** Delete the selected clips. `note` is the caller's, so a cut can say "cut"
   *  rather than reporting a copy and a delete one after the other. */
  const removeSelection = useCallback(
    (note: (deleted: number) => string) => {
      if (!room || selection.length === 0) return;
      const intents = deleteClips(room, selection.map((c) => c.id));
      if (intents.length === 0) return;
      editor.propose(intents);
      say(note(intents.length));
      // The ids are gone; the page's prune effect would catch it on the next
      // room, but the bar should not show a stale count for even one frame.
      setSelected(new Set());
    },
    [room, selection, editor, say, setSelected],
  );

  /** Empty the span — Live's Delete on a time selection: clips inside it go,
   *  clips across its edges are cut back to them. The span stays selected. */
  const clearSelectedSpan = useCallback((): boolean => {
    if (!room || !span) return false;
    const intents = clearSpan(room, span, tempo);
    if (intents === null) {
      say(SPAN_UNCUTTABLE);
      return false;
    }
    if (intents.length > 0) editor.propose(intents);
    setSelected(new Set());
    return true;
  }, [room, span, tempo, editor, say, setSelected]);

  const remove = useCallback(() => {
    if (span) clearSelectedSpan();
    else removeSelection(deletedNote);
  }, [span, clearSelectedSpan, removeSelection]);

  // Cut is deliberately asymmetric: everything selected goes, only what can be
  // carried is carried — and it refuses outright when nothing can be, because a
  // cut you cannot paste back is a delete wearing the wrong name.
  const cut = useCallback(() => {
    if (span) {
      const copied = copySpanToClipboard();
      if (copied && clearSelectedSpan()) say(cutSpanNote(copied.block.length));
      return;
    }
    const carried = copyToClipboard();
    if (carried.length === 0) return;
    removeSelection((deleted) => cutNote(deleted, carried.length));
  }, [span, copySpanToClipboard, clearSelectedSpan, say, copyToClipboard, removeSelection]);

  const nudge = useCallback(
    (steps: number) => {
      if (!room || selection.length === 0 || editor.busy) return;
      const step = grid > 0 ? grid : 1 / 16;
      const delta = nudgeDelta(selection, steps * step);
      if (delta === 0) return;
      const ids = selection.map((c) => c.id);
      // A span that selected these clips travels with them.
      if (land(moveClips(room, ids, delta), ids) && span) {
        setTimeSelection({ ...span, start: span.start + delta, end: span.end + delta });
      }
    },
    [room, selection, editor.busy, grid, land, span, setTimeSelection],
  );

  const insideAt = useCallback(
    (at: number) => selection.some((c) => c.slot === undefined && at > c.startTime && at < c.startTime + c.duration),
    [selection],
  );
  const split = useCallback(() => {
    if (!room) return;
    // Live's ⌘E on a time selection: every clip it crosses is cut at both of
    // its edges, so what was selected becomes a clip of its own.
    if (span) {
      const intents = splitAtSpan(room, span, tempo);
      if (intents.length === 0) say("Nothing crosses the selection's edges to split.");
      else editor.propose(intents);
      return;
    }
    // Live's ⌘E on a clip selection: cut at the insert marker — the point the
    // press or the selection put there — never at the running playhead, which
    // is somewhere else whenever the music is playing.
    if (selection.length === 0) return;
    const at = insertBeats();
    const ids = selection.map((c) => c.id);
    const intents = splitClipsAt(room, ids, at, tempo);
    if (intents.length === 0) {
      say(splitMissNote(room, ids, at));
      return;
    }
    editor.propose(intents);
  }, [room, span, selection, insertBeats, tempo, editor, say]);

  const moveLane = useCallback(
    (direction: -1 | 1) => {
      if (!room || selection.length === 0 || editor.busy) return;
      const ids = selection.map((c) => c.id);
      const intents = moveClipsToLane(room, ids, direction);
      if (intents.length === 0) {
        say(noLaneNote(direction));
        return;
      }
      if (land(intents, ids)) setTimeSelection(null);
    },
    [room, selection, editor.busy, land, say, setTimeSelection],
  );

  const muted = useMemo(() => selection.length > 0 && !muteTarget(selection), [selection]);

  const toggleMute = useCallback(() => {
    if (!room || selection.length === 0) return;
    const next = muteTarget(selection);
    const intents = selection
      .map((c) => setClipFields(room, c.id, { muted: next }))
      .filter((i): i is Intent => i !== null);
    if (intents.length > 0) editor.propose(intents);
  }, [room, selection, editor]);

  const spanCrossesClips = useMemo(() => (room && span ? clipsInSpan(room, span).length > 0 : false), [room, span]);

  return {
    selection,
    span,
    clipboardSize: clipboard ? clipboard.clips.length : 0,
    can: {
      copy: span !== null || copyables.length > 0,
      paste: clipboard !== null,
      // Read when the bar renders; the bar re-renders as the marker moves.
      split: span ? spanCrossesClips : insideAt(insertBeats()),
      act: selection.length > 0 || span !== null,
    },
    muted,
    copy,
    cut,
    paste,
    duplicate,
    split,
    remove,
    nudge,
    moveLane,
    toggleMute,
    note,
  };
}
